import { SitesService } from './../../shared/services/sites.service';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Client } from '@classes/client';
import { Site } from '@classes/site';
import { UiService } from '@services/ui.service';
import { ArticlesService } from '@services/articles.service';
import { Article } from '@classes/article';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { DatatableBaseComponent } from '@components/datatable-base.component';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { MatDialog } from '@angular/material/dialog';
import { ArticleIndexingDialogComponent } from './article-indexing-dialog/article-indexing-dialog.component';
import { PriceIndexationsService } from '@services/price-indexations.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';

@Component({
  selector: 'app-articles',
  templateUrl: './articles.component.html',
  styleUrls: ['./articles.component.scss'],
})
export class ArticlesComponent
  extends DatatableBaseComponent
  implements OnInit
{
  clients: Client[];
  sites: Site[];
  articles: Article[];
  articlesFormated: any[];
  urgencyLevels: DictionaryValue[];
  senders: Address[];
  receivers: Address[];

  isLoading = false;
  isLoadingFilterForm = false;
  showSites = false;
  useSubcontractorPrices = false;

  clientSiteForm: UntypedFormGroup;
  filterForm = new UntypedFormGroup({
    urgency: new UntypedFormControl(),
    sender: new UntypedFormControl(),
    step: new UntypedFormControl(),
  });

  filterValue = null;

  dataSource: MatTableDataSource<any>;
  data: [];
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  displayedColumns = [
    'name',
    'sender',
    'urgency',
    'step',
    'distance',
    'isPricedPerKm',
    'daysWeekDay',
    'daysWeekNight',
    'saturdayDay',
    'saturdayNight',
    'sundayDay',
    'sundayNight',
    'actions',
    'onDelete',
  ];

  spans = [];

  tempRowId = null;
  tempRowCount = null;

  constructor(
    protected uiSrv: UiService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private sitesSrv: SitesService,
    private articlesSrv: ArticlesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private _priceIndexationsService: PriceIndexationsService,
    private authSrv: AuthService,
  ) {
    super(uiSrv);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.isLoadingFilterForm = true;
    this.getRouteData();
  }

  private getRouteData(): void {
    this.route.data.subscribe((data: { clients: Client[] }) => {
      this.clients = data.clients;
      this.initForm();
      this.isLoading = false;
    });

    this.addSubscription(
      this.sitesSrv.sitesListChanged.subscribe((sitesFromSrv) => {
        this.sites = sitesFromSrv;
      }),
    );
  }

  initSub() {
    this.urgencyLevels = [];
    this.senders = [];
    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getUrgencyList().subscribe((urgencyLevelsFromSrv) => {
        this.urgencyLevels = urgencyLevelsFromSrv;
        resolve(true);
      });
    });

    const addressPromise = new Promise((resolve, reject) => {
      this.addressesSrv.getList().subscribe((addressessFromSrv) => {
        this.senders = addressessFromSrv.filter((address) => address.isSender);
        this.receivers = addressessFromSrv.filter(
          (address) => address.isReceiver,
        );
        resolve(true);
      });
    });

    Promise.all([urgencyLevelsPromise, addressPromise]).then(() => {
      this.initFilterForm();
    });

    this.addSubscription(
      this.articlesSrv.articlesFormatedListChanged.subscribe((articles) => {
        this.cacheSpan('sender', (d) => d.sender);
        this.cacheSpan('urgency', (d) => d.urgency);
        const DATA = articles.reduce((current, next) => {
          next.stepsFormated.forEach((step, indexStep) => {
            current.push({
              id: next.id,
              name: next.name,
              sender: next.sender,
              urgency: next.urgency,
              step: step,
              stepsFormated: next.stepsFormated,
              daysWeek: step.daysWeek,
              saturday: step.saturday,
              isPricedPerKm: step.isPricedPerKm,
              distance: step.distance,
              sunday: step.sunday,
              noDays: step.noDays,
              indexStep: indexStep,
            });
          });
          return current;
        }, []);

        this.dataSource.data = DATA || [];

        this.dataSource.sort = this.sort;
        this.onEditFinished();
      }),
    );
  }

  initForm() {
    this.clientSiteForm = this.fb.group({
      client: new UntypedFormControl(null, Validators.required),
      site: new UntypedFormControl(null, Validators.required),
    });

    this.clientSiteForm.get('client').valueChanges.subscribe((client) => {
      this.showSites = false;
      this.useSubcontractorPrices = false; // Reset du toggle au changement de client
      if (client.id) {
        this.sitesSrv.getListByClients(client.id).subscribe((sitesFromSrv) => {
          this.sites = sitesFromSrv;
          this.showSites = true;
        });
      }
    });
  }

  initFilterForm() {
    this.filterForm = this.fb.group({
      sender: new FormControl<Address>(
        this.filterValue?.sender ? this.filterValue.sender : null,
      ),
      urgency: new FormControl<DictionaryValue>(
        this.filterValue?.urgency ? this.filterValue.urgency : null,
      ),
      step: new FormControl<Address>(
        this.filterValue?.step ? this.filterValue.step : null,
      ),
    });

    this.isLoadingFilterForm = false;
  }

  private isSelectedClientSubcontractor(): boolean {
    if(this.clientSiteForm.value.client) {
      return this.clientSiteForm.value.client.isSubcontractor ?? false;
    }
    return false;
  }

  get canShowSubcontractorToggle(): boolean {
    const userRole = this.authSrv.getUser().role.name;
    return (userRole === RoleName.admin || userRole === RoleName.super_admin || userRole === RoleName.bizzdev) 
      && this.isSelectedClientSubcontractor();
  }

  get shouldUseSubcontractorMode(): boolean {
    const userRole = this.authSrv.getUser().role.name;
    if (userRole === RoleName.admin_subcontractor) {
      return true;
    }
    return this.isSelectedClientSubcontractor() && this.useSubcontractorPrices;
  }

  onToggleSubcontractorPrices(): void {
    if (this.clientSiteForm.value.site) {
      this.showArticles();
    }
  }

  onFilter(event): void {
    const formValue = this.filterForm.value;

    this.filterValue = formValue;
    this.filterValue.client = this.clientSiteForm.value.client.id;
    this.filterValue.site = this.clientSiteForm.value.site.id;
    const useSubcontractorMode = this.shouldUseSubcontractorMode;

    this.isLoading = true;
    this.articlesSrv
      .getFormated(this.filterValue, false, useSubcontractorMode)
      .subscribe((articlesFromSrv) => {
        this.articlesFormated = articlesFromSrv;
        this.initTable();
      });
  }

  getControl(control: string): UntypedFormControl {
    return this.clientSiteForm.get(control) as UntypedFormControl;
  }

  showArticles() {
    const formValue = this.filterForm.value;
    this.filterValue = formValue;
    this.filterValue.client = this.clientSiteForm.value.client.id;
    this.filterValue.site = this.clientSiteForm.value.site.id;

    const useSubcontractorMode = this.shouldUseSubcontractorMode;

    this.isLoading = true;
    this.isLoadingFilterForm = true;
    this.articlesSrv
      .getFormated(this.filterValue, false, useSubcontractorMode)
      .subscribe((articlesFromSrv) => {
        this.articlesFormated = articlesFromSrv;
        this.initTable();
        this.initSub();
      });
  }

  showIncompleteTariffs() {
    const formValue = this.filterForm.value;
    this.filterValue = formValue;
    this.filterValue.client = this.clientSiteForm.value.client.id;
    this.filterValue.site = this.clientSiteForm.value.site.id;
    const useSubcontractorMode = this.shouldUseSubcontractorMode;

    this.isLoading = true;
    this.isLoadingFilterForm = true;
    this.articlesSrv
      .getFormated(this.filterValue, true, useSubcontractorMode)
      .subscribe((articlesFromSrv) => {
        this.articlesFormated = articlesFromSrv;
        this.initTable();
        this.initSub();
      });
  }

  private initTable(): void {
    const DATA = this.articlesFormated.reduce((current, next) => {
      next.stepsFormated.forEach((step, indexStep) => {
        current.push({
          id: next.id,
          name: next.name,
          sender: next.sender,
          urgency: next.urgency,
          step: step,
          stepsFormated: next.stepsFormated,
          distance: step.distance,
          isPricedPerKm: step.isPricedPerKm,
          daysWeek: step.daysWeek,
          saturday: step.saturday,
          sunday: step.sunday,
          noDays: step.noDays,
          indexStep: indexStep,
        });
      });
      return current;
    }, []);

    this.data = DATA;
    this.cacheSpan('sender', (d) => d.sender);
    this.cacheSpan('urgency', (d) => d.urgency);

    this.dataSource = new MatTableDataSource(DATA);
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'urgency': {
          return item.urgency.name;
        }
        case 'sender': {
          if (item.sender.address) {
            return item.sender.address.name;
          } else {
            return item.sender.otherAddress;
          }
        }
        case 'steps': {
          if (item.steps[0].address) {
            return item.steps[0].address.name;
          } else {
            return item.steps[0].otherAddress;
          }
        }
        default:
          return item[property];
      }
    };
    this.dataSource.sort = this.sort;

    if (DATA.length === 0) {
      this.filterForm.reset();
      this.filterValue = null;
    }

    this.isLoading = false;
  }

  onEditArticle(article) {
    article.isEdit = true;
    this.editedId = article.step.id;
  }

  onCancel(article) {
    article.isEdit = false;
    this.editedId = null;
  }

  isArticleEdited(article: any): boolean {
    return article.stepsFormated.some((sf) => sf.id === this.editedId);
  }

  findArticleFromStepEdited(article: any): any {
    return article.stepsFormated.find((sf) => sf.id === this.editedId);
  }

  onUpdate(article) {
    article.isEdit = false;
    this.editedId = null;
    //article.stepFormated.find((sf => sf.id === this.editedId)) !== null;

    let articleId = article.id;
    let stepId = article.step.id;

    let tariffs = [];

    article.daysWeek.days = [1, 2, 3, 4, 5];
    article.saturday.days = [6];
    article.sunday.days = [0];
    article.noDays.days = null;
    article.noDays.dayPrice = article.sunday.dayPrice;
    article.noDays.nightPrice = article.sunday.nightPrice;

    tariffs.push(article.daysWeek);
    tariffs.push(article.saturday);
    tariffs.push(article.sunday);
    tariffs.push(article.noDays);

    const useSubcontractorMode = this.shouldUseSubcontractorMode;

    var firstArticle = this.dataSource.data.find(
      (dataArticle) =>
        dataArticle.id === articleId && dataArticle.indexStep === 0,
    );
    if (firstArticle) {
      this.articlesSrv
        .patch(articleId, {
          name: firstArticle.name,
          distance: firstArticle.distance,
          isKilometricPricing: firstArticle.isPricedPerKm,
        })
        .subscribe(() => {});
    }

    this.articlesSrv
      .putStepArticle(articleId, stepId, tariffs, useSubcontractorMode)
      .subscribe((tariffsFromSrv) => {
        this.uiSrv.showSnackbar('Article édité avec succès', false);
        this.isLoading = false;
      });
  }

  generateArticles(site) {
    this.isLoading = true;
    this.articlesSrv.generateArticles(site.id).subscribe((truc) => {
      this.showArticles();
    });
  }

  cacheSpan(key, accessor) {
    for (let i = 0; i < this.data.length; ) {
      let currentValue = accessor(this.data[i]);
      let count = 1;

      // Iterate through the remaining rows to see how many match
      // the current value as retrieved through the accessor.
      for (let j = i + 1; j < this.data.length; j++) {
        if (currentValue != accessor(this.data[j])) {
          break;
        }

        count++;
      }

      if (!this.spans[i]) {
        this.spans[i] = {};
      }

      // Store the number of similar values that were found (the span)
      // and skip i to the next unique row.
      this.spans[i][key] = count;
      i += count;
    }
  }

  getRowSpan(col, index) {
    return this.spans[index] && this.spans[index][col];
  }

  onDeleteArticle(idArticle: string) {
    const confirmation = confirm(
      'Êtes-vous certain de vouloir supprimer cet article ?',
    );

    if (confirmation) {
      this.articlesSrv.delete(idArticle).subscribe(() => {
        this.uiSrv.showSnackbar('billing.articles.list.deleted', true);
      });
    }
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  openIndexingDialog() {
    const formValue = this.filterForm.value;
    this.filterValue = formValue;
    this.filterValue.client = this.clientSiteForm.value.client.id;
    this.filterValue.site = this.clientSiteForm.value.site.id;
    const useSubcontractorMode = this.shouldUseSubcontractorMode;

    this._priceIndexationsService.get(this.filterValue, useSubcontractorMode).subscribe((history) => {
      const dialogRef = this.dialog.open(ArticleIndexingDialogComponent, {
        data: {
          history,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result !== undefined) {
          this._priceIndexationsService
            .post(this.filterValue, result, useSubcontractorMode)
            .subscribe(() => {
              this.showArticles();
            });
          this.uiSrv.showSnackbar(`Indexation lancée à ${result}%.`, false);
        }
      });
    });
  }
}
